import { Button } from '@edsn/pie-components';
import { zodResolver } from '@hookform/resolvers/zod';
import addIcon from '@iconify/icons-material-symbols/add';
import { useQueryClient } from '@tanstack/react-query';
import { t } from 'i18next';
import type { ClipboardEvent, DragEvent, FocusEvent } from 'react';
import { useEffect, useRef, useState } from 'react';
import {
  FormProvider,
  type SubmitHandler,
  useController,
  useFieldArray,
  useForm,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { tv } from 'tailwind-variants';
import { z } from 'zod';
import { MAX_FILE_SIZE } from '#pie/constants/maxFileSize';
import type {
  CommentReferenceType,
  CreateCommentDto,
} from '#pie/internal/api/pie-bff';
import {
  getGetCommentQueryKey,
  usePostCommentCreate,
} from '#pie/internal/api/pie-bff';
import { encodeHtml } from '#pie/utils/encodeHtml';
import { formatBytes } from '#pie/utils/files';
import { isValidXmlCharacters } from '#pie/utils/isValidXmlCharacters';
import { AttachmentCard } from '../attachment-card/AttachmentCard';
import { InputUploadButton } from '../input-upload-button/InputUploadButton';
import { Message } from '../message/Message';
import { Text } from '../text/Text';

export interface ReplyFieldProps {
  referenceId: string;
  referenceType: CommentReferenceType;
  className?: string;
  startExpanded?: boolean;
  maxFileSize?: number;
  canAddAttachments?: boolean;
}

const replySchema = (maxFileSize: number) =>
  z.object({
    description: z
      .string()
      .nonempty(t('zod.errors.required'))
      .refine((value) => isValidXmlCharacters(value), {
        message: t('zod.errors.invalid_xml_characters'),
      }),
    files: z
      .array(
        z.object({
          file: z.instanceof(File).refine((file) => file.size <= maxFileSize, {
            error: () => ({
              message: t('zod.errors.file_too_large', {
                size: formatBytes(maxFileSize),
              }),
            }),
          }),
        }),
      )
      .max(20, t('zod.errors.max_files', { max: 20 })),
  });

type ReplyForm = z.infer<ReturnType<typeof replySchema>>;

export function ReplyField({
  className,
  referenceType,
  startExpanded = false,
  referenceId,
  maxFileSize = MAX_FILE_SIZE,
  canAddAttachments = true,
}: ReplyFieldProps) {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const [isFocused, setIsFocused] = useState(startExpanded);
  const [isFilePickerOpen, setIsFilePickerOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  const resolver = zodResolver(replySchema(maxFileSize));
  const formMethods = useForm<ReplyForm>({
    resolver,
    defaultValues: { description: '', files: [] },
  });
  const {
    control,
    reset,
    formState: { errors, isValid, isSubmitted },
    handleSubmit,
  } = formMethods;

  const {
    field: descriptionField,
    fieldState: { error: descriptionError },
  } = useController({ control, name: 'description' });

  const { remove, replace, fields, append } = useFieldArray<ReplyForm>({
    control,
    name: 'files',
  });

  const descriptionValue = useWatch({ control, name: 'description' }) ?? '';
  const hasText = descriptionValue.length > 0;
  const hasFiles = fields.length > 0;
  const isExpanded = isFocused || hasText || hasFiles || isFilePickerOpen;

  const {
    mutateAsync: commentMutateAsync,
    isSuccess: commentIsSuccess,
    isError: commentIsError,
    isPending: commentIsLoading,
  } = usePostCommentCreate({
    mutation: {
      onSuccess: () => {
        replace([]);
      },
    },
  });

  const addFiles = (files: File[]) => {
    if (files.length) {
      append(files.map((file) => ({ file })));
    }
  };

  const onSubmit: SubmitHandler<ReplyForm> = async ({ description }) => {
    try {
      const data: CreateCommentDto = {
        description: encodeHtml(description),
        referenceId,
        referenceType,
      };
      if (canAddAttachments) {
        data.attachments = fields
          .filter(({ file }) => file)
          .map(({ file }) => file);
      }
      await commentMutateAsync({ data });
      queryClient.invalidateQueries({
        queryKey: getGetCommentQueryKey({ referenceId, referenceType }),
      });
    } catch (err) {
      console.error(err);
    }
  };

  useEffect(() => {
    if (commentIsSuccess) {
      reset();
      setIsFocused(false);
    }
  }, [commentIsSuccess, reset]);

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    if (e.clipboardData.files.length) {
      addFiles([...e.clipboardData.files]);
    }
  };

  const onDrop = (e: DragEvent<HTMLElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.dataTransfer.files.length) {
      addFiles([...e.dataTransfer.files]);
    }
  };

  const onBlur = (e: FocusEvent<HTMLFormElement>) => {
    if (isFilePickerOpen) return;

    const next = e.relatedTarget as Node | null;
    if (!containerRef.current?.contains(next)) {
      setIsFocused(false);
    }
  };

  const s = replyField({ expanded: isExpanded, hasError: !!descriptionError });

  return (
    <div className={s.root({ className })}>
      <FormProvider {...formMethods}>
        <form
          className={s.form()}
          onSubmit={handleSubmit(onSubmit)}
          onDragOver={(e) => e.preventDefault()}
          onDrop={canAddAttachments && isExpanded ? onDrop : undefined}
          onFocus={() => setIsFocused(true)}
          onBlur={onBlur}
        >
          <div ref={containerRef} className={s.container()}>
            <div className={s.textareaWrapper()}>
              <textarea
                {...descriptionField}
                value={descriptionValue}
                aria-label={t('reply_field.comment_input.aria-label')}
                rows={isExpanded ? 8 : 1}
                maxLength={2000}
                onPaste={canAddAttachments ? onPaste : undefined}
                placeholder={t('reply_field.comment_input.placeholder')}
                className={s.textarea()}
              />
              {isExpanded && (
                <span className={s.charCount()}>
                  {descriptionValue.length}/2000
                </span>
              )}
            </div>
            <div className="flex flex-col">
              <div className={s.actionsRow()}>
                {isExpanded && canAddAttachments && (
                  <InputUploadButton
                    icon={addIcon}
                    className="w-fit border-none"
                    multiple
                    size="sm"
                    onChange={addFiles}
                    onDialogOpenChange={setIsFilePickerOpen}
                    variant="tertiarySubtleIcon"
                  >
                    <Text className="text-body-sm py-1">
                      {t('common.attachments.choose_files_other')}
                    </Text>
                  </InputUploadButton>
                )}
                <Button
                  className={s.submitButton()}
                  disabled={!isExpanded || (isSubmitted && !isValid)}
                  isLoading={commentIsLoading}
                  type="submit"
                  action="primary"
                  emphasis="bold"
                  size="sm"
                >
                  {t('common.send')}
                </Button>
              </div>
            </div>
          </div>

          {descriptionError && (
            <Text className={s.errorText()}> {t('zod.errors.required')}</Text>
          )}

          {canAddAttachments && hasFiles && (
            <div className="mb-4 grid grid-cols-3 gap-2">
              {fields.map(({ file }, idx) => (
                <AttachmentCard
                  key={idx}
                  file={file}
                  onDelete={() => remove(idx)}
                  errorMessage={errors.files?.[idx]?.file?.message}
                />
              ))}
            </div>
          )}

          {commentIsError && (
            <Message variant="error">
              <Text>{t('reply_field.error.submit_failed')}</Text>
            </Message>
          )}
        </form>
      </FormProvider>
    </div>
  );
}

export const replyField = tv({
  slots: {
    root: 'flex items-start gap-4',
    form: 'flex w-full flex-col gap-6',
    container:
      'flex rounded-card border bg-surface-disabled transition-all duration-150',
    textareaWrapper: 'relative',
    textarea:
      'w-full resize-none bg-transparent text-body outline-none placeholder:text-content-secondary placeholder:italic py-3 px-5',
    charCount: 'block pb-1 text-right text-xs text-content-tertiary',
    actionsRow: 'flex flex-row items-center',
    submitButton: 'ml-auto w-fit',
    errorText: 'text-content-error -mt-3',
  },
  variants: {
    expanded: {
      true: {
        container: 'flex-col',
        charCount: 'border-b border-border-disabled mx-5',
        textareaWrapper: 'w-full',
        actionsRow: 'justify-between mx-4 my-3',
      },
      false: {
        container: 'flex-row items-center',
        textareaWrapper: 'min-w-0 flex-1',
        actionsRow: 'py-2 pr-3',
      },
    },
    hasError: {
      true: { container: 'border-support-error-bold' },
      false: {
        container:
          'border-transparent focus-within:border-border-minimal-focus',
      },
    },
  },
  compoundVariants: [
    {
      expanded: false,
      hasError: false,
      class: { container: 'hover:border-border-minimal-hover' },
    },
  ],
});
